import { BaseService } from "../BaseService";

// Type definitions for the service
interface AgentChatResponse {
  response: string;
  conversation_id: string;
}

interface AgentChatApiResponse {
  message: string;
  response: Record<string, unknown>;
}

export class DigitalOceanChatService extends BaseService {
  // Send message to specific AI agent via backend (backend handles API key management)
  public async sendAgentMessage(
    agentId: string,
    message: string,
    conversationId?: string | number,
  ): Promise<AgentChatResponse> {
    try {
      const requestBody: {
        messages: Array<{ role: string; content: string }>;
        conversation_id?: number;
      } = {
        messages: [{ role: "user", content: message }],
      };

      // Add conversation_id if provided (convert string to number if needed)
      if (conversationId !== undefined) {
        requestBody.conversation_id =
          typeof conversationId === "string"
            ? parseInt(conversationId)
            : conversationId;
      }

      const response = (await this.postRequest(
        `/ai-agents/${agentId}/chat`,
        requestBody,
      )) as AgentChatApiResponse;

      return {
        response: response.message || "No response received",
        conversation_id: conversationId?.toString() || `chat-${Date.now()}`, // Generate a simple conversation ID
      };
    } catch (error) {
      console.error("Error calling agent API:", error);
      throw new Error("Failed to get response from agent");
    }
  }

  // Send message to DigitalOcean serverless inference via backend
  public async sendInferenceMessage(
    messages: Array<{ role: string; content: string }>,
    model: string = "claude-haiku-4-5",
  ): Promise<string> {
    try {
      // Use backend endpoint for inference
      const response = (await this.postRequest("/ai-agents/inference", {
        model,
        messages,
      })) as { message: string };

      return response.message || "No response received";
    } catch (error) {
      console.error("Error calling inference API:", error);
      throw new Error("Failed to get response from inference API");
    }
  }

  // Get available models via backend
  public async getAvailableModels(): Promise<string[]> {
    try {
      const response = (await this.getRequest("/ai-agents/models")) as {
        models: string[];
      };
      return (
        response.models || [
          "llama3.3-70b-instruct",
          "gpt-4o-mini",
          "claude-haiku-4-5",
        ]
      );
    } catch (error) {
      console.error("Error fetching available models:", error);
      // Return fallback models
      return [
        "llama3.3-70b-instruct",
        "gpt-4o-mini",
        "claude-haiku-4-5",
      ];
    }
  }
}

export const digitalOceanChatService = new DigitalOceanChatService();
